import { type ReactNode } from 'react';
import { Lock } from 'lucide-react';

const LOGO_SRC = '/zerotail-logo-nobg.png';

export type CheckoutOrgBranding = {
  org?: 'llc' | 'labs' | string;
  legal_name?: string;
  short_name?: string;
  pay_label?: string;
  tagline?: string;
  receipt_prefix?: string;
  is_nonprofit?: boolean;
};

export function StatusMessage({
  icon,
  title,
  description,
}: {
  icon?: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      {icon && <div className="mx-auto mb-5">{icon}</div>}
      <h1 className="text-xl font-semibold tracking-tight text-slate-100">{title}</h1>
      <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-zt-muted">{description}</p>
    </div>
  );
}

export default function CheckoutShell({
  children,
  compact = false,
  branding,
}: {
  children: ReactNode;
  compact?: boolean;
  branding?: CheckoutOrgBranding | null;
}) {
  const isLabs = branding?.org === 'labs' || branding?.is_nonprofit;
  const payLabel = branding?.pay_label || (isLabs ? 'ZeroTail Research Labs' : 'ZeroTail Pay');
  const tagline = branding?.tagline
    || (isLabs
      ? '501(c)(3) nonprofit donations & research payments'
      : 'Secure commercial checkout');
  const footer = isLabs
    ? 'Secure donation checkout — ZeroTail Research Labs (501(c)(3))'
    : 'Secure checkout — card details never stored by ZeroTail';

  return (
    <div className="relative flex min-h-screen items-start justify-center overflow-x-hidden bg-zt-bg px-4 py-6 sm:items-center sm:py-12">
      <div
        className={`pointer-events-none absolute inset-0 ${
          isLabs
            ? 'bg-[radial-gradient(ellipse_70%_45%_at_50%_-10%,rgba(56,189,248,0.14),transparent)]'
            : 'bg-[radial-gradient(ellipse_70%_45%_at_50%_-10%,rgba(16,185,129,0.14),transparent)]'
        }`}
        aria-hidden="true"
      />
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent to-transparent ${
          isLabs ? 'via-sky-400/25' : 'via-zt-primary/25'
        }`}
        aria-hidden="true"
      />

      <div className={`relative w-full ${compact ? 'max-w-md' : 'max-w-lg'}`}>
        <header className="mb-6 text-center">
          <img
            src={LOGO_SRC}
            alt={branding?.legal_name || 'ZeroTail'}
            className={`mx-auto h-14 w-auto ${
              isLabs
                ? 'drop-shadow-[0_0_24px_rgba(56,189,248,0.18)]'
                : 'drop-shadow-[0_0_24px_rgba(16,185,129,0.15)]'
            }`}
            width={160}
            height={56}
          />
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-zt-muted">
            {payLabel}
          </p>
          <p className="mt-1 text-xs text-zt-muted/90">{tagline}</p>
          {isLabs && (
            <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.18em] text-sky-300/80">
              Nonprofit · 501(c)(3)
            </p>
          )}
        </header>

        <div className="rounded-2xl border border-zt-border bg-zt-surface/90 shadow-2xl shadow-black/35 backdrop-blur-sm">
          <div
            className={`h-px rounded-t-2xl bg-gradient-to-r from-transparent to-transparent ${
              isLabs ? 'via-sky-400/50' : 'via-zt-primary/50'
            }`}
          />
          <div className={`${compact ? 'p-5 sm:p-6' : 'p-6 sm:p-8'}`}>{children}</div>
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-xs text-zt-muted">
          <Lock
            className={`h-3 w-3 shrink-0 ${isLabs ? 'text-sky-400/70' : 'text-zt-primary/70'}`}
            aria-hidden="true"
          />
          <span>{footer}</span>
        </p>
      </div>
    </div>
  );
}
